import { CustomPopover, usePopover } from '@/components/custom-popover';
import { CustomerType } from '@/enums';
import { useUserProfile } from '@/hooks/useUsers';
import { useXpanseAuth } from '@/hooks/useXpanseAuthentication';
import { getInitials } from '@/utils/getInitials';
import { m } from 'framer-motion';
import { useNavigate } from 'react-router';

import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

export const varHover = (hover = 1.09, tap = 0.97) => ({
  hover: { scale: hover },
  tap: { scale: tap },
});

export default function AccountPopover() {
  const popover = usePopover();
  const navigate = useNavigate();

  const { logout, user } = useXpanseAuth();

  const { organizationCustomerType } = useUserProfile();

  const isAdmin = organizationCustomerType === CustomerType.admin;

  const handleGlobalSettingsClick = () => {
    popover.onClose();
    navigate('/global-settings');
  };

  return (
    <>
      <IconButton
        aria-label="account-button"
        component={m.button}
        whileTap="tap"
        whileHover="hover"
        variants={varHover(1.05)}
        onClick={popover.onOpen}
        sx={{
          width: 40,
          height: 40,
          background: (theme) => alpha(theme.palette.grey[500], 0.08),
          ...(popover.open && {
            background: (theme) =>
              `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
          }),
        }}
      >
        <Avatar
          src={undefined}
          alt={undefined}
          sx={{
            width: 36,
            height: 36,
            border: (theme) => `solid 2px ${theme.palette.background.default}`,
          }}
        >
          {getInitials(user?.name || user?.email || '')}
        </Avatar>
      </IconButton>

      <CustomPopover
        open={popover.open}
        anchorEl={popover.anchorEl}
        onClose={popover.onClose}
        sx={{
          width: 'auto',
        }}
      >
        <Box sx={{ p: 2, pb: 1.5 }}>
          <Typography variant="subtitle2" noWrap>
            {user?.name}
          </Typography>

          <Typography variant="body2" sx={{ color: 'text.secondary' }} noWrap>
            {user?.email}
          </Typography>
        </Box>

        <Divider sx={{ borderStyle: 'dashed' }} />

        {isAdmin && (
          <MenuItem
            onClick={handleGlobalSettingsClick}
            sx={{ m: 1 }}
          >
            <SettingsRoundedIcon
              fontSize="small"
              style={{ marginRight: 8 }}
            />
            Global Settings
          </MenuItem>
        )}

        <Divider sx={{ borderStyle: 'dashed' }} />

        <MenuItem
          onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
          sx={{ m: 1, fontWeight: 'fontWeightBold', color: 'error.main' }}
        >
          <LogoutRoundedIcon
            fontSize="small"
            style={{ marginRight: 8 }}
          />
          Logout
        </MenuItem>
      </CustomPopover>
    </>
  );
}
